import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, Divider, Input, Menu } from '@arco-design/web-react';
import { IconClose, IconDelete, IconEdit } from '@arco-design/web-react/icon';
import { BsQuestionCircle } from 'react-icons/bs';
import {
	getShadowRoot,
	MergeTagBadge,
	useEditorContext,
	useEditorProps,
} from '@bay-email/editor';
import type { Editor } from '@tiptap/core';
import { useForm } from 'react-final-form';
import {
	DYNAMIC_TEXT_FORMAT_OPTIONS,
	dedupeMergeTagKeys,
	getAncestorCustomListingIdx,
	getMergeTagGroup,
	getMergeTagGroupLabel,
	getMergeTagGroupSortWeight,
	getMergeTagLabel,
	hasMergeTagKey,
	normalizeDynamicTextMaxLength,
	t,
} from '@bay-email/core';
import { RAW_ISLAND_SELECTOR } from '@extensions/components/Form/TiptapEditable/RawNodes';
import { getContextMergeTags } from '@extensions/utils/getContextMergeTags';
import { getMergeTagsForContext } from '@extensions/utils/getMergeTagsForContext';
import { matchesMergeTagQuery } from '@extensions/utils/matchesMergeTagQuery';
import { useBlock, useFocusIdx } from '@bay-email/editor';
import { useExtensionProps } from '@extensions/components/Providers/ExtensionProvider';
import BaySearch from '@bay-ui/BaySearch';
import BaySelect from '@bay-ui/BaySelect';
import BayTooltip from '@bay-ui/BayTooltip';
import { useActiveEditable } from '@extensions/components/Form/TiptapEditable/ActiveEditableProvider';
import { unwrapBorderWrap } from '@extensions/components/Form/TiptapEditable/extensions';

// The attribute name used on contenteditable elements to store the form field path
const CONTENT_EDITABLE_IDX_ATTR = 'data-content_editable-idx';

const LINK_MERGE_TAG_KEYS = new Set([
	'listing.url',
	'user.websiteUrl',
	'user.website',
	'user.companyWebsite',
	'user.teamWebsiteAddress',
	'user.officeDomain',
	'unsubscribeLink',
	'legal_compliance.unsubscribeLink',
]);

function isLinkMergeTagKey(tagKey: string): boolean {
	return LINK_MERGE_TAG_KEYS.has(String(tagKey || '').trim());
}

function formatMergeTagLabel(key: string): string {
	const parts = key.split('.');
	const fieldPart = parts[parts.length - 1] || key;
	return fieldPart
		.replace(/[_-]+/g, ' ')
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
		.trim()
		.replace(/\s+/g, ' ')
		.replace(/\b\w/g, c => c.toUpperCase());
}

interface PopoverState {
	tagKey: string;
	tagFallback: string;
	tagFormat: string;
	tagMaxLength: string;
	rect: DOMRect;
	badgeEl: HTMLElement;
	contentEditableEl: HTMLElement;
	fieldName: string;
	// Set only for a BLOCK-level dynamic field (the locked span a Text block
	// renders once its whole content is one merge tag). Those keep their fallback,
	// format and max length in the block's own data rather than on the element, so
	// every edit below writes through the form instead of mutating the DOM.
	// Null for an inline badge, which stays DOM-driven.
	blockIdx: string | null;
	// Set when the badge sits inside a live tiptap editor. Edits then go through
	// the editor (a node-attribute update) instead of mutating the badge DOM, and
	// the editor's own update commit persists them.
	// `island` is set when the badge lives inside a rawBlock/rawInline atom: pos
	// is then the atom's position and badgeIndex locates the badge within its
	// stored html.
	tiptap: { editor: Editor; pos: number; island?: { badgeIndex: number } } | null;
}

// Edits a badge inside a raw island atom by rewriting the atom's stored html.
const updateIslandBadge = (
	editor: Editor,
	pos: number,
	badgeIndex: number,
	mutate: (badge: HTMLElement) => void,
) => {
	const node = editor.state.doc.nodeAt(pos);
	if (!node) return;
	const container = document.createElement('div');
	container.innerHTML = String(node.attrs.html || '');
	const root = container.firstElementChild;
	if (!root) return;
	const badge = root.querySelectorAll<HTMLElement>('.bay-email-merge-tag')[badgeIndex];
	if (!badge) return;
	mutate(badge);
	editor
		.chain()
		.command(({ tr }) => {
			tr.setNodeMarkup(pos, undefined, { ...node.attrs, html: root.outerHTML });
			return true;
		})
		.run();
};

interface ReplacePickerProps {
	onSelect: (key: string) => void;
	onClose: () => void;
}

function ReplacePicker({ onSelect, onClose }: ReplacePickerProps) {
	const [searchQuery, setSearchQuery] = useState('');
	const { mergeTags } = useEditorProps();
	const { values } = useBlock();
	const { focusIdx } = useFocusIdx();
	const { isDesignAdmin } = useExtensionProps();
	const { focusBlock } = useBlock();

	// Same rule as the Text block's Dynamic Fields dropdown: listing fields only
	// exist for blocks with somewhere to get a listing FROM — inside a Custom
	// Listing Wrapper, or inside a Listing section's column. Without this the
	// Replace menu offered a swap that can never resolve, and the two menus
	// disagreed about what was available on the very same block.
	const hasListingScope = React.useMemo(
		() => Boolean(getAncestorCustomListingIdx((values as any)?.content, focusIdx)),
		[values, focusIdx],
	);

	const contextMergeTags = React.useMemo(() => {
		const tags = getContextMergeTags(mergeTags, values, focusIdx);
		const forContext = getMergeTagsForContext(tags, 'no-image-src');
		if (hasListingScope) return forContext;
		return Object.fromEntries(
			Object.entries(forContext || {}).filter(
				([key]) => key !== 'listing' && getMergeTagGroup(key) !== 'listing',
			),
		);
	}, [mergeTags, values, focusIdx, hasListingScope]);

	const normalizedContextMergeTags = React.useMemo(() => {
		const normalized: Record<string, Record<string, any>> = {};

		Object.entries(contextMergeTags || {}).forEach(([groupKey, groupData]) => {
			if (groupData && typeof groupData === 'object') {
				normalized[groupKey] = groupData as Record<string, any>;
				return;
			}

			if (typeof groupData === 'string' || typeof groupData === 'number') {
				const resolvedGroupKey = getMergeTagGroup(groupKey);
				if (!normalized[resolvedGroupKey]) {
					normalized[resolvedGroupKey] = {};
				}
				normalized[resolvedGroupKey][groupKey] = groupData;
			}
		});

		return normalized;
	}, [contextMergeTags]);

	const resolveMergeTagKey = React.useCallback(
		(groupKey: string, fieldKey: string): string => {
			const dottedKey = `${groupKey}.${fieldKey}`;
			if (hasMergeTagKey(dottedKey)) return dottedKey;
			if (hasMergeTagKey(fieldKey)) return fieldKey;
			return dottedKey;
		},
		[],
	);

	const orderedGroupKeys = Object.keys(normalizedContextMergeTags).sort((a, b) => {
		const weightDiff = getMergeTagGroupSortWeight(a) - getMergeTagGroupSortWeight(b);
		if (weightDiff !== 0) return weightDiff;
		return a.localeCompare(b);
	});

	const normalizedSearch = searchQuery.trim().toLowerCase();

	const getFieldLabel = (groupKey: string, fieldKey: string): string => {
		const fullKey = resolveMergeTagKey(groupKey, fieldKey);
		return (
			getMergeTagLabel(fullKey) ??
			fieldKey
				.replace(/[_-]+/g, ' ')
				.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
				.trim()
				.replace(/\b\w/g, c => c.toUpperCase())
		);
	};

	const handleMenuClick = (key: string) => {
		if (!key || !hasMergeTagKey(key)) return;
		onSelect(key);
	};

	return (
		<div
			onMouseDown={e => e.stopPropagation()}
			onClick={e => e.stopPropagation()}
			style={{ minWidth: 260 }}
		>
			<div style={{ padding: '8px 8px 4px' }}>
				<BaySearch
					hideMenu
					searchIcon
					placeholder={t('Search')}
					searchInputValue={searchQuery}
					handleSearchInput={(val: string) => setSearchQuery(val ?? '')}
				/>
			</div>
			<Divider style={{ margin: '4px 0' }} />
			<Menu
				onClickMenuItem={handleMenuClick}
				style={{
					maxHeight: 280,
					overflowY: 'auto',
					border: 'none',
					boxShadow: 'none',
					padding: 0,
				}}
			>
				{orderedGroupKeys.map((groupKey, groupIndex) => {
					const groupData = normalizedContextMergeTags[groupKey];
					if (!groupData || typeof groupData !== 'object') return null;

					const dedupedFieldKeys = dedupeMergeTagKeys(
						Object.keys(groupData).map(fieldKey =>
							resolveMergeTagKey(groupKey, fieldKey),
						),
					).map(fullKey =>
						fullKey.includes('.')
							? fullKey.split('.').slice(1).join('.')
							: fullKey,
					);

					const menuItems = dedupedFieldKeys
						.filter(fieldKey => {
							const fieldValue = groupData[fieldKey];
							const fullKey = resolveMergeTagKey(groupKey, fieldKey);
							const isValidValue =
								typeof fieldValue === 'string' || typeof fieldValue === 'number';
							if (!isValidValue || !hasMergeTagKey(fullKey)) return false;
							if (!normalizedSearch) return true;
							const label = getFieldLabel(groupKey, fieldKey);
							return matchesMergeTagQuery(
								label,
								fieldKey,
								fullKey,
								normalizedSearch,
							);
						})
						.sort((a, b) =>
							getFieldLabel(groupKey, a).localeCompare(
								getFieldLabel(groupKey, b),
								undefined,
								{
									sensitivity: 'base',
									numeric: true,
								},
							),
						)
						.map(fieldKey => (
							<Menu.Item key={resolveMergeTagKey(groupKey, fieldKey)}>
								{getFieldLabel(groupKey, fieldKey)}
							</Menu.Item>
						));

					if (menuItems.length === 0) return null;

					const groupDisplayName = getMergeTagGroupLabel(groupKey);
					return (
						<div
							key={groupKey}
							style={{ padding: '0 8px' }}
						>
							{groupIndex > 0 && <Divider style={{ margin: '4px 0' }} />}
							<Menu.ItemGroup
								title={
									<span
										style={{
											fontSize: 11,
											fontWeight: 700,
											textTransform: 'uppercase',
											color: '#666',
										}}
									>
										{groupDisplayName}
									</span>
								}
							>
								{menuItems}
							</Menu.ItemGroup>
						</div>
					);
				})}
			</Menu>
		</div>
	);
}

export function MergeTagInlinePopover() {
	const { initialized } = useEditorContext();
	const { mergeTagGenerate } = useEditorProps();
	const form = useForm();
	const { active } = useActiveEditable();
	const activeEditorRef = useRef<Editor | null>(null);
	activeEditorRef.current = active?.editor ?? null;

	const [state, setState] = useState<PopoverState | null>(null);
	const [showReplace, setShowReplace] = useState(false);
	const [fallbackValue, setFallbackValue] = useState('');
	const [formatValue, setFormatValue] = useState('string');
	const [maxLengthValue, setMaxLengthValue] = useState('');
	const popoverRef = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState({ x: 0, y: 0, placed: false });

	const root = initialized ? getShadowRoot() : null;

	// A static (non-active) Text render wraps its content in a render-only
	// div.bay-text-border-wrap. Strip it before reverting so it never lands in
	// the stored content.
	const syncContent = useCallback(
		(contentEditableEl: HTMLElement, fieldName: string) => {
			if (!contentEditableEl || !fieldName) return;
			const html = unwrapBorderWrap(contentEditableEl.innerHTML);
			const reverted = MergeTagBadge.revert(html, mergeTagGenerate);
			form.change(fieldName, reverted);
		},
		[form, mergeTagGenerate],
	);

	const closePopover = useCallback(
		(shouldSync = true) => {
			// Only inline badges live in the content HTML. Syncing a block-level
			// field would write its RESOLVED text back over the template and destroy
			// the tag.
			if (shouldSync && state && !state.blockIdx && !state.tiptap) {
				syncContent(state.contentEditableEl, state.fieldName);
			}
			setState(null);
			setShowReplace(false);
			setFallbackValue('');
			setFormatValue('string');
			setMaxLengthValue('');
			setPosition({ x: 0, y: 0, placed: false });
		},
		[state, syncContent],
	);

	// Badge click listener on shadow root (capture phase to intercept before MergeTagBadgePrompt)
	useEffect(() => {
		if (!root) return;

		const handleBadgeClick = (e: Event) => {
			const target = e.target as HTMLElement;
			// A resolved block-level dynamic field is not a badge — the Text block
			// renders it as a locked span holding real text, so the class test alone
			// meant clicking one did nothing at all. closest() so a click anywhere
			// inside the resolved value still opens it.
			const lockedEl = target.closest(
				'[data-dynamic-locked="true"]',
			) as HTMLElement | null;
			const isInlineBadge = target.classList.contains('bay-email-merge-tag');
			if (!isInlineBadge && !lockedEl) return;

			e.stopPropagation();

			const badgeEl = (isInlineBadge ? target : lockedEl) as HTMLElement;

			const contentEditableEl = badgeEl.closest(
				'[contenteditable="true"]',
			) as HTMLElement;
			const fieldName = contentEditableEl?.getAttribute(CONTENT_EDITABLE_IDX_ATTR) || '';

			// The contenteditable's field path is `<blockIdx>.data.value.content`, so
			// the block that owns a locked span is that path minus its tail.
			const blockIdx = isInlineBadge
				? null
				: fieldName.replace(/.data.value.content$/, '') || null;
			const readBlockValue = (key: string) =>
				blockIdx
					? String(
							form.getFieldState(`${blockIdx}.data.value.${key}`)?.value ?? '',
						)
					: '';

			const tagKey = blockIdx
				? badgeEl.getAttribute('data-dynamic-tag') || ''
				: badgeEl.getAttribute('data-tag-key') || badgeEl.textContent || '';
			const tagFallback = blockIdx
				? readBlockValue('dynamicTextPlaceholder')
				: badgeEl.getAttribute('data-tag-fallback') || '';
			const tagFormat = blockIdx
				? readBlockValue('dynamicTextFormat') || 'string'
				: badgeEl.getAttribute('data-tag-format') || 'string';
			const tagMaxLength = blockIdx
				? readBlockValue('dynamicTextMaxLength')
				: badgeEl.getAttribute('data-tag-max-length') || '';

			const rect = badgeEl.getBoundingClientRect();

			// A static editable (not yet the active editor) is plain legacy DOM, so
			// only a badge inside the live editor takes the tiptap path.
			const liveEditor = activeEditorRef.current;
			let tiptap: PopoverState['tiptap'] = null;
			if (liveEditor && !liveEditor.isDestroyed && liveEditor.view.dom.contains(badgeEl)) {
				try {
					tiptap = { editor: liveEditor, pos: liveEditor.view.posAtDOM(badgeEl, 0) };
					const islandEl = badgeEl.parentElement?.closest(RAW_ISLAND_SELECTOR);
					if (islandEl && liveEditor.view.dom.contains(islandEl)) {
						const p = liveEditor.view.posAtDOM(islandEl, 0);
						const islandPos = [p, p - 1].find(
							c =>
								c >= 0 &&
								['rawBlock', 'rawInline'].includes(
									liveEditor.state.doc.nodeAt(c)?.type.name || '',
								),
						);
						if (islandPos !== undefined) {
							const atomDom = liveEditor.view.nodeDOM(islandPos);
							const badgeIndex =
								atomDom instanceof Element
									? Array.from(atomDom.querySelectorAll('.bay-email-merge-tag')).indexOf(badgeEl)
									: -1;
							if (badgeIndex >= 0) {
								tiptap = { editor: liveEditor, pos: islandPos, island: { badgeIndex } };
							}
						}
					}
				} catch {
					tiptap = null;
				}
			}

			setState({
				tagKey,
				tagFallback,
				tagFormat,
				tagMaxLength,
				rect,
				badgeEl,
				contentEditableEl,
				fieldName,
				blockIdx,
				tiptap,
			});
			setShowReplace(false);
			setFallbackValue(tagFallback);
			setFormatValue(tagFormat || 'string');
			setMaxLengthValue(tagMaxLength);
			setPosition({ x: 0, y: 0, placed: false });
		};

		root.addEventListener('click', handleBadgeClick, true);
		return () => root.removeEventListener('click', handleBadgeClick, true);
	}, [root]);

	// Close on outside click in main document
	useEffect(() => {
		if (!state) return;

		const handleOutsideClick = (e: MouseEvent) => {
			const target = e.target as Node;
			if (
				target instanceof Element &&
				(target.closest('.bay-select__menu') ||
					target.closest('.bay-select__menu-portal') ||
					target.closest('.bay-select__control'))
			) {
				return;
			}
			if (popoverRef.current?.contains(target)) return;
			closePopover(true);
		};

		document.addEventListener('mousedown', handleOutsideClick, true);
		return () => document.removeEventListener('mousedown', handleOutsideClick, true);
	}, [state, closePopover]);

	// Close on Escape key
	useEffect(() => {
		if (!state) return;
		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') closePopover(true);
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [state, closePopover]);

	// Close if canvas scrolls (position would be stale)
	useEffect(() => {
		if (!state || !root) return;
		const handleScroll = () => closePopover(true);
		root.addEventListener('scroll', handleScroll, true);
		return () => root.removeEventListener('scroll', handleScroll, true);
	}, [state, root, closePopover]);

	// Compute viewport-safe position after render
	useEffect(() => {
		if (!state || !popoverRef.current || position.placed) return;

		const el = popoverRef.current;
		const { bottom, left, top, right } = state.rect;
		const popW = el.offsetWidth || 280;
		const popH = el.offsetHeight || 200;
		const viewW = window.innerWidth;
		const viewH = window.innerHeight;

		let x = left;
		let y = bottom + 8;

		if (x + popW > viewW - 8) x = Math.max(8, right - popW);
		if (y + popH > viewH - 8) y = Math.max(8, top - popH - 8);

		setPosition({ x, y, placed: true });
	}, [state, position.placed, showReplace]);

	// A block-level dynamic field keeps its config in the block, not on the
	// element — write there and skip the DOM entirely.
	const writeBlockValue = (key: string, value: string | number | null) => {
		if (!state?.blockIdx) return false;
		form.change(`${state.blockIdx}.data.value.${key}`, value);
		return true;
	};

	const updateTiptapBadge = (
		attrs: Record<string, unknown>,
		islandMutate?: (badge: HTMLElement) => void,
	) => {
		if (!state?.tiptap) return false;
		const { editor, pos, island } = state.tiptap;
		if (island) {
			if (islandMutate) updateIslandBadge(editor, pos, island.badgeIndex, islandMutate);
			return true;
		}
		editor.chain().setNodeSelection(pos).updateAttributes('mergeTag', attrs).run();
		return true;
	};

	const handleFallbackChange = (newFallback: string) => {
		if (!state) return;
		setFallbackValue(newFallback);
		if (writeBlockValue('dynamicTextPlaceholder', newFallback)) return;
		if (
			updateTiptapBadge({ tagFallback: newFallback || null }, badge => {
				if (newFallback) badge.setAttribute('data-tag-fallback', newFallback);
				else badge.removeAttribute('data-tag-fallback');
			})
		)
			return;
		// Update the badge DOM immediately for live feedback
		if (newFallback) {
			state.badgeEl.setAttribute('data-tag-fallback', newFallback);
		} else {
			state.badgeEl.removeAttribute('data-tag-fallback');
		}
		// Form sync happens on close
	};

	const handleFormatChange = (newFormat: string | number | undefined) => {
		if (!state) return;
		const normalizedFormat = String(newFormat || 'string');
		setFormatValue(normalizedFormat);

		if (state.blockIdx) {
			writeBlockValue('dynamicTextFormat', normalizedFormat);
			// Max length only applies to plain text, same rule as the DOM path below.
			if (normalizedFormat !== 'string' && maxLengthValue) {
				setMaxLengthValue('');
				writeBlockValue('dynamicTextMaxLength', '');
			}
			return;
		}

		if (state.tiptap) {
			const isPlain = !normalizedFormat || normalizedFormat === 'string';
			updateTiptapBadge(
				{
					tagFormat: isPlain ? null : normalizedFormat,
					...(!isPlain && maxLengthValue ? { tagMaxLength: null } : {}),
				},
				badge => {
					if (!isPlain) {
						badge.setAttribute('data-tag-format', normalizedFormat);
						if (maxLengthValue) badge.removeAttribute('data-tag-max-length');
					} else {
						badge.removeAttribute('data-tag-format');
					}
				},
			);
			if (!isPlain && maxLengthValue) setMaxLengthValue('');
			return;
		}

		if (normalizedFormat && normalizedFormat !== 'string') {
			state.badgeEl.setAttribute('data-tag-format', normalizedFormat);
			if (maxLengthValue) {
				setMaxLengthValue('');
				state.badgeEl.removeAttribute('data-tag-max-length');
			}
		} else {
			state.badgeEl.removeAttribute('data-tag-format');
		}
	};

	const handleMaxLengthChange = (newValue: string) => {
		if (!state) return;
		const sanitized = String(newValue || '')
			.replace(/\D+/g, '')
			.slice(0, 4);
		setMaxLengthValue(sanitized);

		const normalized = normalizeDynamicTextMaxLength(sanitized);
		if (state.blockIdx) {
			writeBlockValue(
				'dynamicTextMaxLength',
				normalized !== null && formatValue === 'string' ? normalized : '',
			);
			return;
		}
		if (state.tiptap) {
			updateTiptapBadge(
				{
					tagMaxLength: normalized !== null && formatValue === 'string' ? String(normalized) : null,
				},
				badge => {
					if (normalized !== null && formatValue === 'string') {
						badge.setAttribute('data-tag-max-length', String(normalized));
					} else {
						badge.removeAttribute('data-tag-max-length');
					}
				},
			);
			return;
		}
		if (normalized !== null && formatValue === 'string') {
			state.badgeEl.setAttribute('data-tag-max-length', String(normalized));
		} else {
			state.badgeEl.removeAttribute('data-tag-max-length');
		}
	};

	const handleRemove = () => {
		if (!state) return;
		if (state.blockIdx) {
			// Clearing the tag is what removes a block-level dynamic field; the block
			// then renders its own content again. Ripping the span out of the DOM
			// would be undone on the next render.
			writeBlockValue('dynamicTextTag', '');
		} else if (state.tiptap) {
			const { editor, pos, island } = state.tiptap;
			if (island) {
				updateIslandBadge(editor, pos, island.badgeIndex, badge => badge.remove());
			} else {
				editor.chain().setNodeSelection(pos).deleteSelection().run();
			}
		} else {
			state.badgeEl.parentNode?.removeChild(state.badgeEl);
			syncContent(state.contentEditableEl, state.fieldName);
		}
		setState(null);
		setShowReplace(false);
		setFallbackValue('');
		setFormatValue('string');
		setMaxLengthValue('');
		setPosition({ x: 0, y: 0, placed: false });
	};

	const handleReplace = (newKey: string) => {
		if (!state) return;
		if (state.blockIdx) {
			writeBlockValue('dynamicTextTag', `{{${newKey}}}`);
			writeBlockValue('dynamicTextPlaceholder', '');
		} else if (state.tiptap) {
			const newDisplayLabel = formatMergeTagLabel(newKey);
			updateTiptapBadge(
				{
					tagKey: newKey,
					label: newDisplayLabel,
					tagFallback: null,
				},
				badge => {
					badge.textContent = newDisplayLabel;
					badge.setAttribute('value', newDisplayLabel);
					badge.setAttribute('data-tag-key', newKey);
					badge.removeAttribute('data-tag-fallback');
				},
			);
		} else {
			const newDisplayLabel = formatMergeTagLabel(newKey);
			state.badgeEl.textContent = newDisplayLabel;
			state.badgeEl.setAttribute('value', newDisplayLabel);
			state.badgeEl.setAttribute('data-tag-key', newKey);
			state.badgeEl.removeAttribute('data-tag-fallback');
			syncContent(state.contentEditableEl, state.fieldName);
		}
		setState(null);
		setShowReplace(false);
		setFallbackValue('');
		setFormatValue('string');
		setMaxLengthValue('');
		setPosition({ x: 0, y: 0, placed: false });
	};

	if (!state) return null;

	const displayLabel =
		getMergeTagLabel(state.tagKey) ?? formatMergeTagLabel(state.tagKey);
	const formatOptions = DYNAMIC_TEXT_FORMAT_OPTIONS.map(option => ({
		value: option.value,
		label: t(option.label),
	}));
	const selectedFormatOption =
		formatOptions.find(option => option.value === formatValue) || formatOptions[0];
	const isLinkTag = isLinkMergeTagKey(state.tagKey);
	const fallbackFieldLabel = isLinkTag ? t('Link text') : t('Fallback text');
	const fallbackFieldHelpText = isLinkTag
		? t('Enter the clickable text shown for this link.')
		: t('Enter a word or phrase to display if this specific detail is empty.');
	const fallbackFieldPlaceholder = isLinkTag ? t('Enter link text...') : 'Enter...';

	const popoverContent = (
		<div
			ref={popoverRef}
			onMouseDown={e => e.stopPropagation()}
			onClick={e => e.stopPropagation()}
			style={{
				position: 'fixed',
				left: position.x,
				top: position.y,
				zIndex: 99999,
				background: '#fff',
				border: '1px solid #e5e6eb',
				borderRadius: 10,
				boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
				width: 280,
				fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
				overflow: 'hidden',
				// Hide until positioned to avoid flash
				visibility: position.placed ? 'visible' : 'hidden',
			}}
		>
			{/* Header */}
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					padding: '12px 14px 8px',
					borderBottom: '1px solid #f0f0f0',
				}}
			>
				<div style={{ flex: 1, minWidth: 0 }}>
					<div
						style={{
							fontSize: 13,
							fontWeight: 600,
							color: '#1a1a2e',
							marginBottom: 2,
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
						}}
					>
						{displayLabel}
					</div>
					<div
						style={{
							fontSize: 11,
							color: '#888',
							fontFamily: 'monospace',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
						}}
					>
						{`{{${state.tagKey}}}`}
					</div>
				</div>
				<button
					onClick={() => closePopover(true)}
					style={{
						background: 'none',
						border: 'none',
						cursor: 'pointer',
						color: '#aaa',
						padding: '2px 4px',
						borderRadius: 4,
						display: 'flex',
						alignItems: 'center',
						marginLeft: 8,
					}}
					aria-label={t('Close')}
				>
					<IconClose style={{ fontSize: 14 }} />
				</button>
			</div>

			{/* Replace picker (toggleable) */}
			{showReplace ? (
				<div>
					<div
						style={{
							padding: '8px 14px 4px',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
						}}
					>
						<span
							style={{
								fontSize: 11,
								fontWeight: 600,
								color: '#555',
								textTransform: 'uppercase',
							}}
						>
							{t('Replace with')}
						</span>
						<button
							onClick={() => setShowReplace(false)}
							style={{
								background: 'none',
								border: 'none',
								cursor: 'pointer',
								color: '#aaa',
								padding: '0 2px',
							}}
							aria-label={t('Cancel')}
						>
							<IconClose style={{ fontSize: 12 }} />
						</button>
					</div>
					<ReplacePicker
						onSelect={handleReplace}
						onClose={() => setShowReplace(false)}
					/>
				</div>
			) : (
				<>
					{/* Fallback input */}
					<div style={{ padding: '10px 14px 8px' }}>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: 6,
								marginBottom: 6,
							}}
						>
							<span
								style={{
									fontSize: 11,
									fontWeight: 600,
									color: '#555',
									textTransform: 'uppercase',
								}}
							>
								{fallbackFieldLabel}
							</span>
							<BayTooltip
								placement='top'
								overlay={fallbackFieldHelpText}
								overlayStyle={{ zIndex: 100100 }}
							>
								<span
									style={{
										display: 'inline-flex',
										alignItems: 'center',
										color: '#86909c',
										cursor: 'help',
									}}
									aria-label={t('Fallback text help')}
								>
									<BsQuestionCircle size={12} />
								</span>
							</BayTooltip>
						</div>
						<Input
							placeholder={fallbackFieldPlaceholder}
							value={fallbackValue}
							onChange={handleFallbackChange}
							style={{ borderRadius: 6, fontSize: 13, height: 38 }}
						/>
					</div>

					<div style={{ padding: '4px 14px 8px' }}>
						<div
							style={{
								fontSize: 11,
								fontWeight: 600,
								color: '#555',
								textTransform: 'uppercase',
								marginBottom: 6,
							}}
						>
							{t('Text format')}
						</div>
						<BaySelect
							optionsArray={formatOptions}
							controlledValue={selectedFormatOption}
							onChangeFunc={(option: any) => handleFormatChange(option?.value)}
							isClearable={false}
							allowSearch={false}
							menuPortalTarget={document.body}
							menuPosition='fixed'
							customStyles={{
								menuPortal: (base: any) => ({
									...base,
									zIndex: 100100,
								}),
							}}
						/>
					</div>

					<div style={{ padding: '0 14px 10px' }}>
						<div
							style={{
								fontSize: 11,
								fontWeight: 600,
								color: '#555',
								textTransform: 'uppercase',
								marginBottom: 6,
							}}
						>
							{t('Max length')}
						</div>
						<Input
							placeholder={t('No limit')}
							value={maxLengthValue}
							onChange={handleMaxLengthChange}
							disabled={formatValue !== 'string'}
							style={{ borderRadius: 6, fontSize: 13, height: 38 }}
						/>
					</div>

					{/* Actions */}
					<Divider style={{ margin: '0' }} />
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							padding: '8px 14px 10px',
						}}
					>
						<Button
							size='small'
							type='outline'
							icon={<IconEdit />}
							onClick={() => {
								setShowReplace(true);
								setPosition(p => ({ ...p, placed: false }));
							}}
							style={{ borderRadius: 6 }}
						>
							{t('Replace')}
						</Button>
						<Button
							size='small'
							type='text'
							status='danger'
							icon={<IconDelete />}
							onClick={handleRemove}
							style={{ borderRadius: 6, color: '#f53f3f' }}
						>
							{t('Remove')}
						</Button>
					</div>
				</>
			)}
		</div>
	);

	return createPortal(popoverContent, document.body);
}
